import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid-pro";
import {
  CommissionIcon,
  GKButton,
  GKDataGrid,
  GKTypography,
  Grid2,
  NoData,
  PlusIcon,
  Show,
  StatusField,
  Typography,
} from "components";
import useGenerateQueryParams from "hooks/useGenerateQueryParams";
import { useFetchQuery } from "hooks/useQueries";
import { InvoiceList } from "interfaces/Revenue";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { updateFilter } from "store/slices/tableSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import {
  handleReturnPositiveNegative,
  moneyCommaSeparator,
  numberToCurrency,
} from "utils";
import { extraColumnsField, handleDownloadFile } from "utils/helpers";
import { FinancialYearDropdown, financialYearFilter } from "../utils";
import CommissionDetailsModal from "./CommissionDetailsModal";
import FetchInvoiceModal from "./FetchInvoiceModal";

interface Props {
  name?: string;
  tableName?: string;
  portfolioId?: string;
  commissionLink?: string | null;
  partnerId?: string;
  isButtonDisable?: boolean;
}

const CommissionManager = (props: Props) => {
  const dispatch = useAppDispatch();

  const {
    name,
    tableName,
    portfolioId,
    commissionLink,
    partnerId,
    isButtonDisable = false,
  } = props;

  const navigate = useNavigate();

  const commonFilter = useAppSelector((state) => state?.table?.commonFilter);

  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [commissionDetails, setCommissionDetails] = useState(null);
  const [openFetchInvoiceModal, setOpenFetchInvoiceModal] = useState(false);

  const requestBody = useGenerateQueryParams(name || "commissionManager", {
    portfolio_id: portfolioId,
    partner_id: partnerId,
  });

  const { data, isFetching } = useFetchQuery({
    key: ["COMMISSION_MANAGER_LIST", requestBody],
    route: "/api/revenue/revenue-commission-manager/",
    requestBody,
  });

  const { data: commissionCardDetails, isFetching: commissionCardLoading } =
    useFetchQuery({
      key: ["COMMISSION_MANAGER_CARD_DETAILS"],
      route: "/api/revenue/revenue-commission-manager/cards/",
      requestBody: { portfolio_id: portfolioId, partnerId: partnerId },
    });

  const updateFYFilter = (filterName: string, val: any) => {
    const targetName = filterName || "commissionManager";
    dispatch(
      updateFilter({
        name: targetName,
        filter: {
          customFilter: {
            financialYear: val?.value,
            start_date: val.dates.startDate,
            end_date: val.dates.endDate,
          },
        },
      }),
    );
  };

  const handleDownloadInvoice = (
    event: React.MouseEvent<HTMLElement>,
    params: GridRenderCellParams<InvoiceList>,
  ) => {
    event.stopPropagation();
    handleDownloadFile({
      link: params?.row?.invoicePdf,
      name: params?.row?.invoiceNumber,
    });
  };

  const handleAddCommission = () => {
    portfolioId || partnerId
      ? navigate(commissionLink)
      : setOpenFetchInvoiceModal(true);
  };

  const columns: GridColDef[] = [
    {
      ...extraColumnsField(
        `Portfolio Name (${data?.data?.totalCount || 0})`,
        "clientName",
        250,
        1,
      ),
      renderCell: (params) => {
        return (
          <Grid2
            container
            display={"flex"}
            flexDirection={"column"}
            alignItems={"flex-start"}
          >
            <Typography
              fontWeight={700}
              maxWidth={"100%"}
              textOverflow={"ellipsis"}
              overflow={"hidden"}
            >
              {params?.row?.clientName}
            </Typography>
            <Typography fontSize={12}>
              {params?.row?.portfolioName} - {params?.row?.portfolioId}
            </Typography>
          </Grid2>
        );
      },
    },
    {
      ...extraColumnsField("Partner Name", "partnerName", 150, 1),
      valueGetter: (row) => row?.row?.partnerName || "-",
    },
    {
      ...extraColumnsField("Invoice Amount/No.", "invoiceAmount", 170, 1),
      type: "number",
      renderCell: (params) => (
        <Grid2
          container
          display={"flex"}
          flexDirection={"column"}
          alignItems={"flex-end"}
        >
          <Typography>
            {numberToCurrency(+params?.row?.invoiceAmount)}
          </Typography>
          <Typography
            fontSize={12}
            fontWeight={500}
            color="var(--neutral-500)"
            sx={{ cursor: "pointer" }}
            onClick={(event) => handleDownloadInvoice(event, params)}
          >
            {params?.row?.invoiceNumber}
          </Typography>
        </Grid2>
      ),
    },
    {
      ...extraColumnsField(
        "commission for Mgt. Fees (₹)",
        "managementFeeCommission",
        230,
        1,
      ),
      valueGetter: (row) =>
        moneyCommaSeparator(row?.row?.managementFeeCommission),
      type: "number",
    },
    {
      ...extraColumnsField(
        "commission for Perf. Fees (₹)",
        "performanceFeeCommission",
        240,
        1,
      ),
      valueGetter: (row) =>
        moneyCommaSeparator(row?.row?.performanceFeeCommission),
      type: "number",
    },
    {
      ...extraColumnsField("total commission (₹)", "commissionAmount", 210, 1),
      valueGetter: (row) => moneyCommaSeparator(row?.row?.commissionAmount),
      type: "number",
    },
    {
      ...extraColumnsField("Status", "status", 100, 1),
      renderCell: (params) => (
        <StatusField statusString={params?.row?.status} />
      ),
    },
  ];

  const filterColumn = columns.filter((item) =>
    portfolioId
      ? item.field !== "clientName"
      : partnerId
        ? item.field !== "partnerName"
        : item,
  );

  return (
    <Grid2 container spacing={2}>
      <Grid2 size={12} textAlign={"right"}>
        <FinancialYearDropdown
          updateFilter={updateFYFilter}
          tableKey={name || "commissionManager"}
          selectedValue={
            financialYearFilter?.find(
              (dataItem) =>
                dataItem.value ===
                (name
                  ? commonFilter?.[name]?.customFilter?.financialYear
                  : commonFilter?.commissionManager?.customFilter
                      ?.financialYear),
            )?.name
          }
        />
      </Grid2>
      <Grid2
        container
        size={12}
        spacing={0}
        borderRadius={"8px"}
        border={"1px solid var(--neutral-100)"}
      >
        <Grid2 size={3} p="12px 16px">
          <Typography fontSize={12} fontWeight={400} color="var(--neutral-700)">
            Total Commissions to be Paid
          </Typography>
          <GKTypography
            fontSize={16}
            fontWeight={600}
            color="var(--black)"
            loading={commissionCardLoading}
          >
            {numberToCurrency(
              commissionCardDetails?.data?.data?.totalCommissionAmount,
            )}
          </GKTypography>
        </Grid2>
        <Grid2
          size={3}
          p="12px 16px"
          borderLeft={"1px solid var(--neutral-100)"}
        >
          <Typography fontSize={12} fontWeight={400} color="var(--neutral-700)">
            Paid Commission Amount
          </Typography>
          <GKTypography
            fontSize={16}
            fontWeight={600}
            color={handleReturnPositiveNegative(
              commissionCardDetails?.data?.data?.totalPaidCommissionAmount,
            )}
            loading={commissionCardLoading}
          >
            {numberToCurrency(
              commissionCardDetails?.data?.data?.totalPaidCommissionAmount,
            )}
          </GKTypography>
        </Grid2>
        <Grid2
          size={3}
          p="12px 16px"
          borderLeft={"1px solid var(--neutral-100)"}
        >
          <Typography fontSize={12} fontWeight={400} color="var(--neutral-700)">
            Unpaid Commission Amount
          </Typography>
          <GKTypography
            fontSize={16}
            fontWeight={600}
            color={handleReturnPositiveNegative(
              commissionCardDetails?.data?.data?.totalUnpaidCommissionAmount,
            )}
            loading={commissionCardLoading}
          >
            {numberToCurrency(
              commissionCardDetails?.data?.data?.totalUnpaidCommissionAmount,
            )}
          </GKTypography>
        </Grid2>
        <Grid2
          size={3}
          p="12px 16px"
          borderLeft={"1px solid var(--neutral-100)"}
        >
          <Typography fontSize={12} fontWeight={400} color="var(--neutral-700)">
            Paid This Month
          </Typography>
          <GKTypography
            fontSize={16}
            fontWeight={600}
            color="var(--black)"
            loading={commissionCardLoading}
          >
            {numberToCurrency(
              commissionCardDetails?.data?.data?.totalPaidCommissionThisMonth,
            )}
          </GKTypography>
        </Grid2>
      </Grid2>
      <Grid2 size={12}>
        <GKDataGrid
          loading={isFetching}
          columns={filterColumn}
          rows={data?.data?.data}
          maxHeight={"calc(100vh - 312px)"}
          totalCount={data?.data?.totalCount}
          name={name || "commissionManager"}
          tableName={tableName || "commission_manager"}
          disabledColumns={["clientName", "partnerName", "status"]}
          onRowClick={(params) => {
            setCommissionDetails(params.row);
            setOpenDetailsModal(true);
          }}
          headerComponent={
            <GKButton
              variant="contained"
              disabled={isButtonDisable}
              onClick={handleAddCommission}
              startIcon={<PlusIcon />}
            >
              Add Commission
            </GKButton>
          }
          emptyComponent={
            <NoData
              text="Nothing Here Yet"
              subText="Commissions will appear here once client invoices are generated and assigned to partners through revenue-sharing fee plans."
              customImg={
                <CommissionIcon
                  height={80}
                  width={80}
                  color="var(--neutral-100)"
                />
              }
            />
          }
        />
      </Grid2>

      <Show.When isTrue={openDetailsModal}>
        <CommissionDetailsModal
          open={openDetailsModal}
          setOpen={setOpenDetailsModal}
          commissionDetails={commissionDetails}
        />
      </Show.When>

      <Show.When isTrue={openFetchInvoiceModal}>
        <FetchInvoiceModal
          open={openFetchInvoiceModal}
          setOpen={setOpenFetchInvoiceModal}
        />
      </Show.When>
    </Grid2>
  );
};

export default CommissionManager;
